(() => {
  'use strict';
  if (window.MagmaContentLayout) {
    window.MagmaContentLayout.mount(document);
    return;
  }
  const instances = new Map();
  const mobile = window.matchMedia('(max-width: 767px)');
  const setProperty = (element, name, value) => {
    if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
  };
  const number = (styles, name, fallback) => {
    const value = parseFloat(styles.getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };
  class ContentLayout {
    constructor(root) {
      this.root = root;
      this.kind = root.hasAttribute('data-mg-section-layout') ? 'section' : root.hasAttribute('data-mg-collection-layout') ? 'collection' : 'group';
      this.content = this.kind === 'section' ? root.querySelector('.mg-section-grid') : this.kind === 'collection' ? root.querySelector('.mg-carousel-track') : root.querySelector(':scope > .mg-group-content');
      this.frame = 0;
      this.observer = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.schedule()) : null;
      this.observer?.observe(this.content || root);
      this.alignedFields = new Set();
      this.observedFields = new Set();
      if (root.hasAttribute('data-mg-align-grid-fields')) {
        this.fieldObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.schedule()) : null;
        this.mutations = new MutationObserver(() => this.schedule());
        this.mutations.observe(root, { childList:true, characterData:true, subtree:true, attributes:true, attributeFilter:['hidden','class','style','src','data-mg-row-field'] });
      }
      this.layout();
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.layout();
      });
    }
    layout() {
      if (!this.root.isConnected) return;
      if (this.content && this.kind !== 'group') this.layoutGrid();
      else if (this.content) this.layoutGroup();
      else this.layoutStep();
    }
    layoutGroup() {
      const layout = this.root.dataset.mgLayout;
      if (layout !== 'grid' && layout !== 'row') return;
      if (layout === 'row' && this.root.dataset.mgRowSizing === 'content') return;
      const width = this.content.getBoundingClientRect().width;
      if (width <= 0) return;
      const children = [...this.content.children].filter(child => !child.hidden && !child.matches('script,style,template') && getComputedStyle(child).display !== 'none');
      const styles = getComputedStyle(this.root);
      const contentStyles = getComputedStyle(this.content);
      const stackedRow = layout === 'row' && mobile.matches && this.root.dataset.mgMobileLayout !== 'row';
      const configured = stackedRow ? 1 : Math.max(1, Math.round(number(styles, mobile.matches ? '--mg-group-mobile-columns' : '--mg-group-columns', 1)));
      const minimum = Math.max(0, number(styles, mobile.matches ? '--mg-group-mobile-min-column' : '--mg-group-min-column', mobile.matches ? 120 : 160));
      const gap = Math.max(0, parseFloat(contentStyles.columnGap) || 0);
      const capacity = minimum > 0 ? Math.max(1, Math.floor((width + gap + 0.5) / (minimum + gap))) : configured;
      const columns = Math.min(configured, Math.max(1, children.length), capacity);
      setProperty(this.content, '--mg-group-effective-columns', String(columns));
      setProperty(this.content, '--mg-row-cell-width', Math.max(0, (width - (columns - 1) * gap) / columns) + 'px');
    }
    layoutGrid() {
      if (this.kind === 'section' && this.root.dataset.mgSectionLayout !== 'grid') return;
      const styles = getComputedStyle(this.root);
      const contentStyles = getComputedStyle(this.content);
      const width = this.content.getBoundingClientRect().width;
      if (width <= 0) return;
      const children = [...this.content.children].filter(child => !child.hidden && !child.matches('script,style,template') && getComputedStyle(child).display !== 'none');
      const property = mobile.matches ? '--mg-mobile-columns' : innerWidth < 1024 ? '--mg-tablet-columns' : '--mg-columns';
      const configured = Math.max(1, Math.round(number(styles, property, 3)));
      let minimum = Math.max(0, number(styles, mobile.matches ? '--mg-mobile-min-column' : '--mg-min-column', mobile.matches ? 120 : 160));
      if (this.root.hasAttribute('data-mg-align-grid-fields')) {
        const titles = [...this.content.querySelectorAll('[data-mg-row-field="title"]')];
        // Keep compact columns readable when text is enlarged independently
        // of the viewport. Never reduce the author's text size to fit a row.
        let scale = 1;
        for (const title of titles) {
          const textStyles = getComputedStyle(title), size=parseFloat(textStyles.fontSize)||0;
          const configuredSize=number(textStyles,mobile.matches?'--mg-mobile-font-size':'--mg-font-size',size);
          minimum=Math.max(minimum,size*8);
          if (configuredSize>0) scale=Math.max(scale,size/configuredSize);
        }
        setProperty(this.root,'--mg-row-type-scale',String(scale));
      }
      const gap = Math.max(0, parseFloat(contentStyles.columnGap) || 0);
      const capacity = minimum > 0 ? Math.max(1, Math.floor((width + gap + 0.5) / (minimum + gap))) : configured;
      const cells = children.filter(child => !child.classList.contains('mg-span--full'));
      const itemLimit = this.kind === 'collection' && this.root.dataset.stretch !== 'true' ? configured : Math.max(1, cells.length || children.length);
      let columns = Math.min(configured, itemLimit, capacity);
      if (this.kind === 'section' && columns === 2 && configured === 2 && innerWidth >= 1024 && minimum > 0) {
        const first = number(styles, '--mg-first-column', 50);
        const second = number(styles, '--mg-second-column', 50);
        if ((width - gap) * Math.min(first, second) / (first + second) < minimum) columns = 1;
      }
      if (this.kind === 'section') {
        setProperty(this.content, '--mg-effective-columns', String(columns));
        if (this.root.dataset.mgLastRow === 'center') {
          const spans = '.mg-span--full,.mg-span--2,.mg-span--3,.mg-span--4,.mg-span--5,.mg-span--6,.mg-mobile-span--full,.mg-mobile-span--2,.mg-mobile-span--3';
          const asymmetric = configured === 2 && number(styles,'--mg-first-column',50) !== number(styles,'--mg-second-column',50);
          const uniform = !asymmetric && !children.some(child=>child.matches(spans));
          // Native spanning and unequal column tracks take precedence. The
          // centered-row mode is only for uniformly sized, unspanned cells.
          this.root.toggleAttribute('data-mg-centered-cells',uniform);
          if (uniform) setProperty(this.content, '--mg-row-cell-width', Math.max(0, (width - (columns - 1) * gap) / columns) + 'px');
          else this.content.style.removeProperty('--mg-row-cell-width');
        }
        this.root.classList.toggle('mg-section--single-column', columns === 1);
      } else {
        const previous = this.root.style.getPropertyValue('--mg-collection-effective-columns');
        setProperty(this.root, '--mg-collection-effective-columns', String(columns));
        if (previous !== String(columns)) this.root.dispatchEvent(new CustomEvent('magma:layout-change'));
      }
      if (this.kind === 'section' && this.root.hasAttribute('data-mg-cell-dividers')) {
        let previous = null;
        const tracks = getComputedStyle(this.content).gridTemplateColumns.split(' ').map(value => parseFloat(value)).filter(Number.isFinite);
        if (contentStyles.direction === 'rtl') tracks.reverse();
        let edge = this.content.getBoundingClientRect().left;
        const gridLines = tracks.slice(0, -1).map(track => {
          edge += track;
          const line = edge + gap / 2;
          edge += gap;
          return line;
        });
        const visible = children.map(child => ({ child, rect: child.getBoundingClientRect() }));
        visible.sort((a, b) => {
          const sameRow = a.rect.top < b.rect.bottom - 1 && a.rect.bottom > b.rect.top + 1;
          return sameRow ? a.rect.left - b.rect.left : a.rect.top - b.rect.top;
        });
        for (const { child, rect } of visible) {
          const sameRow = previous && rect.top < previous.bottom - 1 && rect.bottom > previous.top + 1;
          child.toggleAttribute('data-mg-column-divider', Boolean(sameRow));
          if (sameRow) {
            const follows = rect.left + rect.width / 2 > previous.left + previous.width / 2;
            const midpoint = follows ? (previous.right + rect.left) / 2 : (rect.right + previous.left) / 2;
            const divider = gridLines.reduce((nearest, line) => Math.abs(line - midpoint) < Math.abs(nearest - midpoint) ? line : nearest, gridLines[0] ?? midpoint);
            setProperty(child, '--mg-cell-divider-offset', (divider - rect.left) + 'px');
          } else child.style.removeProperty('--mg-cell-divider-offset');
          previous = rect;
        }
      }
      this.alignGridFields(children);
    }
    alignGridFields(children) {
      if (!this.root.hasAttribute('data-mg-align-grid-fields')) return;
      const visible = children.filter(cell => cell.getBoundingClientRect().width > 0).map(cell => ({ cell, rect:cell.getBoundingClientRect() }));
      visible.sort((a,b) => Math.abs(a.rect.top-b.rect.top) < 1 ? a.rect.left-b.rect.left : a.rect.top-b.rect.top);
      const rows = [];
      for (const item of visible) {
        let row = rows.find(row => Math.abs(row.top-item.rect.top) < 1);
        if (!row) rows.push(row = {top:item.rect.top,items:[]});
        row.items.push(item.cell);
      }
      const active = new Set();
      const targets = new Set();
      for (const row of rows) {
        const slots = new Map();
        for (const cell of row.items) for (const field of cell.querySelectorAll('[data-mg-row-field]')) {
          if (getComputedStyle(field).display === 'none' || !field.getClientRects().length) continue;
          const key = field.dataset.mgRowField;
          if (!key) continue;
          let depth = 0;
          for (let p=field.parentElement; p && p!==cell; p=p.parentElement) depth++;
          if (!slots.has(key)) slots.set(key, {depth,fields:[]});
          const slot = slots.get(key); slot.depth=Math.max(slot.depth,depth); slot.fields.push(field);
          active.add(field);
          const content = field.matches('.mg-group') ? field.querySelector(':scope > .mg-group-content') : null;
          if (content) targets.add(content);
          else if (field.children.length) for (const child of field.children) targets.add(child);
          else targets.add(field);
        }
        // Inner title/supporting fields settle first. Their containing copy
        // group can then reserve equal space even when subtext is absent.
        for (const slot of [...slots.values()].sort((a,b)=>b.depth-a.depth)) {
          const natural = slot.fields.map(field => {
            const styles = getComputedStyle(field);
            const content = field.matches('.mg-group') ? field.querySelector(':scope > .mg-group-content') : null;
            let height = content ? content.getBoundingClientRect().height : 0;
            if (!content) {
              const children = [...field.children].filter(child=>getComputedStyle(child).display!=='none');
              if (children.length) height=Math.max(...children.map(child=>child.getBoundingClientRect().bottom))-Math.min(...children.map(child=>child.getBoundingClientRect().top));
              else { const range=document.createRange(); range.selectNodeContents(field); height=range.getBoundingClientRect().height; }
            }
            return height+(parseFloat(styles.paddingTop)||0)+(parseFloat(styles.paddingBottom)||0)+(parseFloat(styles.borderTopWidth)||0)+(parseFloat(styles.borderBottomWidth)||0);
          });
          const height = Math.ceil(Math.max(0,...natural)*64)/64;
          for (const field of slot.fields) setProperty(field,'--mg-row-field-height',height+'px');
        }
      }
      for (const field of this.alignedFields) if (!active.has(field)) field.style.removeProperty('--mg-row-field-height');
      this.alignedFields=active;
      for (const target of this.observedFields) if (!targets.has(target)) this.fieldObserver?.unobserve(target);
      for (const target of targets) if (!this.observedFields.has(target)) this.fieldObserver?.observe(target);
      this.observedFields=targets;
    }
    layoutStep() {
      const marker = this.root.querySelector(':scope > .mg-step-inner > .mg-step-number');
      if (!marker || !this.root.classList.contains('mg-step-position--beside')) {
        this.root.classList.remove('mg-step--compact');
        return;
      }
      const width = this.root.getBoundingClientRect().width;
      if (width <= 0) return;
      const styles = getComputedStyle(this.root);
      const markerStyles = getComputedStyle(marker);
      const markerWidth = Math.max(number(styles, '--mg-step-marker-width', 36), parseFloat(markerStyles.minWidth) || 0, marker.getBoundingClientRect().width);
      const gap = Math.max(0, number(styles, '--mg-step-gap', 16));
      this.root.classList.toggle('mg-step--compact', width < markerWidth + gap + 180);
    }
    destroy() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.fieldObserver?.disconnect();
      this.mutations?.disconnect();
      for (const field of this.alignedFields) field.style.removeProperty('--mg-row-field-height');
    }
  }
  function mount(container) {
    const roots = [...container.querySelectorAll('[data-mg-layout],[data-mg-step],[data-mg-section-layout],[data-mg-collection-layout]')];
    if (container.matches?.('[data-mg-layout],[data-mg-step],[data-mg-section-layout],[data-mg-collection-layout]')) roots.unshift(container);
    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, new ContentLayout(root));
    });
  }
  const relayout = () => instances.forEach(instance => instance.schedule());
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of instances) {
      if (event.target === root || event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  });
  window.addEventListener('resize', relayout);
  if (document.fonts) {
    document.fonts.ready.then(relayout);
    document.fonts.addEventListener?.('loadingdone', relayout);
  }
  mobile.addEventListener('change', relayout);
  window.MagmaContentLayout = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
})();
